(ns practitest.frontend.components2.dashboard.dashboard-tab-modal
  "New / Edit Dashboard Tab modal: name, private, tab level filter and external access.
   Shared by the dashboards settings page and the summary page."
  (:require [reagent.core :as reagent]
            [re-frame.core :as re-frame]
            [clojure.string :as str]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components.selector :as selector]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.dashboard.tab-events :as tab-events]))

(def ^:private tab-filter-help-url "https://www.practitest.com/help/reporting/dashboard/")

(defn- tab->form [tab]
  {:name             (:name tab)
   :private          (boolean (:private tab))
   :tab_filter_field (:tab_filter_field tab)
   :external_enabled (boolean (:external_enabled tab))})

;; External access = the tab (and its items' embed codes) can be viewed without login via the external URL.
;; A new tab has no key yet, so only the checkbox (and the save hint) is shown.
(defn- external-access-section [form tab new? on-regenerate-key]
  (let [copied? (reagent/atom false)]
    (fn [form tab new? on-regenerate-key]
      (let [url (:external_url tab)]
        [:div.dashboard-tab-external-box
         [:label.m-checkbox-label
          [:input.m-checkbox
           {:type      "checkbox"
            :checked   (:external_enabled @form)
            :on-change #(swap! form assoc :external_enabled (.. % -target -checked))}]
          [:span
           [:span.dashboard-tab-external-title.dashboard-tab-edit-label
            (get-translation "dashboards.edit-tab.external" "Allow external access to this tab (without login)")]
           [:span.dashboard-tab-external-hint
            (get-translation "dashboards.edit-tab.external-hint"
                             "Anyone with the external URL can view this tab's data without signing in. Required for embedding its dashboard items.")]]]
         (when (and new? (:external_enabled @form))
           [:div.dashboard-tab-external-url-section
            [:span.dashboard-tab-edit-hint
             (get-translation "dashboards.edit-tab.external-save-hint" "The URL becomes active after you save.")]])
         (when (and (not new?) (:external_enabled @form))
           [:div.dashboard-tab-external-url-section
            [:span.dashboard-tab-edit-label (get-translation "dashboards.edit-tab.external-url" "External URL:")]
            [:div.dashboard-tab-external-url-row
             [:input.form-control
              {:type      "text"
               :read-only true
               :value     (or url "")}]
             [:a.dashboard-tab-external-url-open {:href url :target "_blank" :rel "noopener noreferrer"}
              [icons/mux-external-link]]]
            [:div.dashboard-tab-external-url-actions
             (when on-regenerate-key
               [buttons/button
                {:tertiary? true
                 :size      "small"
                 :icon      [icons/mux-refresh]
                 :text      (get-translation "dashboards.edit-tab.regenerate-key" "Regenerate Key")
                 :tooltip   (get-translation "dashboards.edit-tab.regenerate-key-hint"
                                             "The current URL and existing item embed codes will stop working.")
                 :on-click  on-regenerate-key}])
             [buttons/button
              {:tertiary? true
               :size      "small"
               :icon      [icons/mux-copy]
               :text      (if @copied?
                            (get-translation "dashboard-item.embed.copied" "Copied!")
                            (get-translation "dashboards.edit-tab.copy-url" "Copy URL"))
               :on-click  #(do (js/navigator.clipboard.writeText url)
                               (reset! copied? true)
                               (js/setTimeout (fn [] (reset! copied? false)) 1500))}]]
            (when (not= (:external_enabled @form) (boolean (:external_enabled tab)))
              [:span.dashboard-tab-edit-hint
               (get-translation "dashboards.edit-tab.external-save-hint" "The URL becomes active after you save.")])])]))))

(defn dashboard-tab-modal
  [{:keys [tab]}]
  (let [form (reagent/atom (tab->form tab))]
    ;; container-ref - optional, renders the modal inside that element (e.g. while it's in browser fullscreen)
    (fn [{:keys [open? new? tab tab-filter-fields saving? error on-save on-cancel on-regenerate-key container-ref]}]
      (let [blank-name? (str/blank? (:name @form))
            selected    (some #(when (= (:value %) (:tab_filter_field @form)) %) tab-filter-fields)]
        [modal/bootstrap-modal
         {:is-open?         open?
          :container-ref    container-ref
          :on-request-close on-cancel
          :close-button?    true
          :centered?        true
          :class            "dashboard-tab-edit-modal"}
         [:div.dashboard-tab-edit-modal-header
          [:span.dashboard-tab-edit-modal-icon [icons/mux-dashboards]]
          [:div
           [:div.dashboard-tab-edit-modal-title
            (if new?
              (get-translation "dashboards.new-tab.title" "New Dashboard Tab")
              (get-translation "dashboards.edit-tab.title" "Edit Dashboard Tab"))]
           [:div.dashboard-tab-edit-modal-subtitle
            (get-translation "dashboards.edit-tab.subtitle"
                             "Tabs group dashboard items for one audience or one slice of the project.")]]]
         [:div.dashboard-tab-edit-modal-body
          [:div.dashboard-tab-edit-row
           [:label.dashboard-tab-edit-label (get-translation "dashboards.edit-tab.name" "Tab Name:")]
           [:input.form-control
            {:type      "text"
             :value     (or (:name @form) "")
             :on-change #(swap! form assoc :name (.. % -target -value))}]]

          [:div.dashboard-tab-edit-row
           [:label.m-checkbox-label.dashboard-tab-edit-label
            [:input.m-checkbox
             {:type      "checkbox"
              :checked   (:private @form)
              :disabled  (:private_locked tab)
              :on-change #(swap! form assoc :private (.. % -target -checked))}]
            (get-translation "dashboards.edit-tab.private" "Private Tab")]
           (when (:private_locked tab)
             [:span.dashboard-tab-edit-hint
              (get-translation "dashboards.edit-tab.private-locked" "This Dashboard has to be private/public")])]

          [:div.dashboard-tab-edit-row
           [:label.dashboard-tab-edit-label
            (get-translation "dashboards.edit-tab.tab-filter" "Tab Level Filter")
            [:span.dashboard-tab-edit-optional (get-translation "dashboards.edit-tab.optional" "(Optional)")]]
           [selector/new-select
            {:items       tab-filter-fields
             :key-fn      :value
             :label-fn    :label
             :value       selected
             :clearable?  true
             :placeholder (get-translation "dashboards.edit-tab.tab-filter-placeholder" "Select field")
             :on-change   #(swap! form assoc :tab_filter_field (:value %))}]
           [:span.dashboard-tab-edit-hint
            (get-translation "dashboards.edit-tab.tab-filter-hint" "Only list fields can be used")
            " "
            [:a {:href tab-filter-help-url :target "_blank"}
             (get-translation "dashboards.edit-tab.learn-how" "Learn how")]]]

          [external-access-section form tab new? on-regenerate-key]

          (when error
            [:div.dashboard-tab-edit-error error])]
         [:div.dashboard-tab-edit-modal-footer
          [buttons/button {:text     (get-translation "generic.button.cancel" "Cancel")
                           :on-click on-cancel}]
          [buttons/button {:primary?  true
                           :text      (if new?
                                        (get-translation "generic.button.create" "Create")
                                        (get-translation "generic.button.save" "Save"))
                           :disabled? (or saving? blank-name?)
                           :on-click  #(on-save @form)}]]]))))

;; The page's state under `db-key` holds :tabs, :edit-modal, :new-tab and :tab-filter-fields
(re-frame/reg-sub
 ::page-field
 (fn [db [_ db-key k]]
   (get-in db [db-key k])))

(re-frame/reg-sub
 ::edit-modal-tab
 (fn [db [_ db-key]]
   (let [{:keys [tabs edit-modal]} (get db db-key)]
     (when-let [tab-id (:tab-id edit-modal)]
       (some #(when (= (:id %) tab-id) %) tabs)))))

(defn tab-edit-modal
  "New/Edit tab modal wired to the page's state under `db-key`; shown while `[db-key :edit-modal]` is set.
   `on-created`, `on-saved` and `on-key-regenerated` are the page's success events, `on-failure` its regenerate-key failure event.
   `container-ref` is passed through to `dashboard-tab-modal`."
  [{:keys [db-key container-ref on-created on-saved on-key-regenerated on-failure]}]
  (let [tab               @(re-frame/subscribe [::edit-modal-tab db-key])
        {:keys [new? saving? error]} @(re-frame/subscribe [::page-field db-key :edit-modal])
        new-tab           @(re-frame/subscribe [::page-field db-key :new-tab])
        tab-filter-fields @(re-frame/subscribe [::page-field db-key :tab-filter-fields])
        on-cancel         #(re-frame/dispatch [::tab-events/close-edit-modal db-key])]
    (cond
      new?
      ^{:key "new-tab"}
      [dashboard-tab-modal
       {:open?             true
        :new?              true
        :tab               (select-keys new-tab [:private :private_locked])
        :tab-filter-fields tab-filter-fields
        :saving?           saving?
        :error             error
        :on-save           #(re-frame/dispatch [::tab-events/create-tab db-key % on-created])
        :on-cancel         on-cancel
        :container-ref     container-ref}]

      tab
      ^{:key (:id tab)}
      [dashboard-tab-modal
       {:open?             true
        :tab               tab
        :tab-filter-fields tab-filter-fields
        :saving?           saving?
        :error             error
        :on-save           #(re-frame/dispatch [::tab-events/save-tab db-key (:id tab) % on-saved])
        :on-cancel         on-cancel
        :container-ref     container-ref
        :on-regenerate-key #(re-frame/dispatch [::tab-events/regenerate-key (:id tab)
                                                {:on-success on-key-regenerated
                                                 :on-failure on-failure}])}])))
